/**
 * @fileoverview Asset Entity Configuration
 *
 * Complete configuration for asset instances (voorzieningen) including
 * navigation logic, editable fields, and custom UI sections for detail pages.
 *
 * @module Configs/Entities/Asset
 */
import {
  Stack,
  Buildings,
  HouseLine,
  MapPin,
  Tag,
  Package,
  Calendar,
  CheckCircle,
  WarningCircle,
  HashStraight,
  Note,
  Wrench,
  LinkSimple,
  Door,
  Trash,
  Screwdriver,
  ShieldCheck,
  FolderOpen,
} from '@phosphor-icons/react';
import { toast } from 'react-toastify';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { UniversalEntityConfig } from 'shared/types/universalEntityConfig';
import { AssetStatus } from 'shared/types/asset';
import { getIconByValue } from 'components/dueDiligence/IconPicker/icon-categories';

/**
 * Helper functions for asset display
 */
const getStatusColor = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'active':
    case 'actief':
      return 'bg-green-100 text-green-700 border-green-300';
    case 'inactive':
    case 'inactief':
      return 'bg-gray-100 text-gray-600 border-gray-300';
    case 'maintenance':
    case 'onderhoud':
      return 'bg-yellow-100 text-yellow-700 border-yellow-300';
    case 'defect':
      return 'bg-red-100 text-red-700 border-red-300';
    case 'retired':
    case 'buiten gebruik':
      return 'bg-gray-100 text-gray-700 border-gray-300';
    case 'decommissioned':
    case 'ontmanteld':
      return 'bg-red-100 text-red-800 border-red-300';
    case 'pending_installation':
    case 'wacht op installatie':
      return 'bg-blue-100 text-blue-700 border-blue-300';
    default:
      return 'bg-gray-100 text-gray-700 border-gray-300';
  }
};

const formatDate = (dateString: string | Date | undefined) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('nl-NL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const getStatusIcon = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'active':
    case 'actief':
      return CheckCircle;
    case 'inactive':
    case 'inactief':
      return CheckCircle;
    case 'maintenance':
    case 'onderhoud':
      return Wrench;
    case 'defect':
      return WarningCircle;
    case 'retired':
    case 'decommissioned':
      return WarningCircle;
    case 'pending_installation':
      return Wrench;
    default:
      return CheckCircle;
  }
};

/**
 * Complete asset entity configuration
 */
export const ASSET_ENTITY_CONFIG: UniversalEntityConfig = {
  // ============ Basic Identity ============
  entityName: 'asset',
  displayName: 'Voorziening',
  displayNamePlural: 'Voorzieningen',
  entityPrefix: 'A',
  titleFirst: true,
  getTitleIcon: (entity) => {
    if (entity?.assetType?.icon) {
      return getIconByValue(entity.assetType.icon);
    }
    return null;
  },

  // ============ API Configuration ============
  api: {
    fetchOne: (id) => `${API_URL}/assets/${id}`,
    fetchAll: () => `${API_URL}/assets`,
    update: (id) => `${API_URL}/assets/${id}`,
    delete: (id) => `${API_URL}/assets/${id}`,
  },

  // ============ Fields ============
  fields: [
    // Name
    {
      id: 'name',
      label: 'Naam',
      type: 'text',
      icon: Tag,

      tableConfig: {
        displayInTable: true,
        sortable: true,
        width: '250px',
      },

      detailConfig: {
        editable: true,
        required: true,
        placeholder: 'Voer voorziening naam in',
        section: 'main',
      },

      getValue: (asset) => asset.name,
      formatDisplay: (value) => value || '-',
      buildUpdatePayload: (value) => ({ name: value }),
    },

    // Identifier
    {
      id: 'identifier',
      label: 'Identificatie',
      type: 'text',
      icon: HashStraight,

      tableConfig: {
        displayInTable: true,
        sortable: true,
        width: '150px',
      },

      detailConfig: {
        editable: true,
        placeholder: 'bijv. Serienummer, Code',
        section: 'main',
      },

      getValue: (asset) => asset.identifier,
      formatDisplay: (value) => value || '-',
      buildUpdatePayload: (value) => ({ identifier: value }),
    },

    // Status
    {
      id: 'status',
      label: 'Status',
      type: 'picker',
      icon: CheckCircle,
      pickerMode: 'custom-tags',
      pickerConfig: {
        customOptions: [
          { label: 'Actief', value: 'active' },
          { label: 'Inactief', value: 'inactive' },
          { label: 'Onderhoud', value: 'maintenance' },
          { label: 'Defect', value: 'defect' },
          { label: 'Buiten gebruik', value: 'retired' },
          { label: 'Ontmanteld', value: 'decommissioned' },
          { label: 'Wacht op installatie', value: 'pending_installation' },
        ],
      },

      tableConfig: {
        displayInTable: true,
        sortable: true,
        filterEnabled: true,
      },

      detailConfig: {
        editable: true,
        section: 'main',
      },

      getValue: (asset) => asset.status,
      formatDisplay: (value) => {
        switch (value) {
          case AssetStatus.ACTIVE:
            return 'Actief';
          case AssetStatus.INACTIVE:
            return 'Inactief';
          case AssetStatus.MAINTENANCE:
            return 'Onderhoud';
          case AssetStatus.DEFECT:
            return 'Defect';
          case AssetStatus.RETIRED:
            return 'Buiten gebruik';
          case AssetStatus.DECOMMISSIONED:
            return 'Ontmanteld';
          case AssetStatus.PENDING_INSTALLATION:
            return 'Wacht op installatie';
          default:
            return value || '-';
        }
      },
      buildUpdatePayload: (value) => ({ status: value }),
    },

    // Building
    {
      id: 'building',
      label: 'Gebouw',
      type: 'picker',
      icon: Buildings,
      pickerMode: 'building',

      tableConfig: {
        displayInTable: true,
        sortable: true,
        filterEnabled: true,
      },

      detailConfig: {
        editable: true,
        section: 'main',
        chipColor: 'border-moss-primary/30 bg-moss-primary/10 text-moss-primary',
      },

      getValue: (asset) => asset.building,
      formatDisplay: (building) => building?.id || '-',
      buildUpdatePayload: (value) => ({ buildingId: value?.id || null }),
    },

    // Unit
    {
      id: 'unit',
      label: 'Unit',
      type: 'picker',
      icon: HouseLine,
      pickerMode: 'unit',

      tableConfig: {
        displayInTable: true,
        sortable: true,
      },

      detailConfig: {
        editable: true,
        section: 'main',
        chipColor: 'border-moss-primary/30 bg-moss-primary/10 text-moss-primary',
      },

      getValue: (asset) => asset.unit,
      formatDisplay: (unit) => (unit?.id ? `Unit ${unit.id}` : '-'),
      buildUpdatePayload: (value) => ({ unitId: value?.id || null }),
      extractContextValue: (asset) => asset.building?.id,
    },

    // Room (picker linked to unit)
    {
      id: 'room',
      label: 'Ruimte',
      type: 'picker',
      pickerMode: 'room',
      icon: Door,

      pickerConfig: {
        searchPlaceholder: 'Zoek ruimte...',
        allowRemoval: true,
        apiConfig: {
          endpoint: '/units/{contextValue}/rooms',
          displayField: 'roomType',
          valueField: 'id',
        },
      },

      tableConfig: {
        displayInTable: false,
      },

      detailConfig: {
        editable: true,
        section: 'main',
      },

      getValue: (asset) => asset.room,
      formatDisplay: (room) => room?.roomType || room?.name || '-',
      buildUpdatePayload: (value) => ({ roomId: value?.id || null }),
      extractContextValue: (asset) => asset.unitId || asset.unit?.id,
    },

    // Location (text field — where exactly is the asset placed)
    {
      id: 'location',
      label: 'Waar geplaatst',
      type: 'text',
      icon: MapPin,

      tableConfig: {
        displayInTable: false,
      },

      detailConfig: {
        editable: true,
        placeholder: 'bijv. Keuken, Woonkamer, Badkamer',
        section: 'main',
      },

      getValue: (asset) => asset.location,
      formatDisplay: (value) => value || '-',
      buildUpdatePayload: (value) => ({ location: value }),
    },

    // Installation Date
    {
      id: 'installationDate',
      label: 'Installatiedatum',
      type: 'picker',
      pickerMode: 'document-date',
      icon: Calendar,

      tableConfig: {
        displayInTable: false,
      },

      detailConfig: {
        editable: true,
        section: 'main',
      },

      getValue: (asset) => asset.installationDate,
      formatDisplay: (value) => formatDate(value),
      buildUpdatePayload: (value) => ({ installationDate: value || null }),
    },

    // Warranty Expiry Date
    {
      id: 'warrantyExpiryDate',
      label: 'Garantie vervalt',
      type: 'picker',
      pickerMode: 'expiration-date',
      icon: ShieldCheck,

      tableConfig: {
        displayInTable: false,
      },

      detailConfig: {
        editable: true,
        section: 'main',
      },

      getValue: (asset) => asset.warrantyExpiryDate,
      formatDisplay: (value) => formatDate(value),
      buildUpdatePayload: (value) => ({ warrantyExpiryDate: value || null }),
    },

    // Installer (linked to ContactCompany)
    {
      id: 'installer',
      label: 'Installateur',
      type: 'picker',
      pickerMode: 'supplier',
      icon: Screwdriver,

      tableConfig: {
        displayInTable: false,
      },

      getValue: (asset) => asset.installer,
      formatDisplay: (installer) => installer?.name || '-',
      buildUpdatePayload: (value) => ({ installerId: value?.id || null }),

      detailConfig: {
        editable: true,
        section: 'main',
        chipColor: 'border-neutral-primary/30 bg-neutral-primary/10 text-neutral-primary',
        customDisplayRenderer: (asset) => {
          const company = asset.installer;
          if (!company?.id) return <span className="text-muted-foreground italic">Niet ingesteld</span>;
          return (
            <a
              href={`/company/${company.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline font-medium"
              onClick={(e) => e.stopPropagation()}
            >
              {company.name}
            </a>
          );
        },
      },
    },

    // Maintenance Company (linked to ContactCompany)
    {
      id: 'maintenanceCompany',
      label: 'Onderhoudsfirma',
      type: 'picker',
      pickerMode: 'supplier',
      icon: Wrench,

      tableConfig: {
        displayInTable: false,
      },

      detailConfig: {
        editable: true,
        section: 'main',
        customDisplayRenderer: (asset) => {
          const company = asset.maintenanceCompany;
          if (!company?.id) return <span className="text-muted-foreground italic">Niet ingesteld</span>;
          return (
            <a
              href={`/company/${company.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline font-medium"
              onClick={(e) => e.stopPropagation()}
            >
              {company.name}
            </a>
          );
        },
      },

      getValue: (asset) => asset.maintenanceCompany,
      formatDisplay: (company) => company?.name || '-',
      buildUpdatePayload: (value) => ({ maintenanceCompanyId: value?.id || null }),
    },

    // Parent Asset (for hierarchies like head meter → sub-meters)
    {
      id: 'parentAsset',
      label: 'Bovenliggende voorziening',
      type: 'picker',
      pickerMode: 'asset',
      icon: LinkSimple,

      pickerConfig: {
        searchPlaceholder: 'Zoek bovenliggende voorziening...',
        allowRemoval: true,
        apiConfig: {
          endpoint: '/assets/potential-parents/{entityId}',
          displayField: 'name',
          valueField: 'id',
        },
      },

      tableConfig: {
        displayInTable: false,
      },

      detailConfig: {
        editable: true,
        section: 'main',
        customDisplayRenderer: (asset) => {
          const parent = asset.parentAsset;
          if (!parent?.id) return null;
          return (
            <a
              href={`/asset/${parent.id}`}
              className="text-primary hover:underline font-medium flex items-center gap-1.5"
              onClick={(e) => e.stopPropagation()}
            >
              {parent.name || parent.id}
              {parent.fieldValues?.meternummer && (
                <span className="text-xs text-muted-foreground font-normal">#{parent.fieldValues.meternummer}</span>
              )}
            </a>
          );
        },
      },

      getValue: (asset) => asset.parentAsset,
      formatDisplay: (parent) => parent?.name || '-',
      buildUpdatePayload: (value) => ({ parentAssetId: value?.id || null }),
      extractContextValue: (asset) => asset.id,
    },

    // Notes
    {
      id: 'notes',
      label: 'Notities',
      type: 'textarea',
      icon: Note,

      tableConfig: {
        displayInTable: false,
      },

      detailConfig: {
        editable: true,
        placeholder: 'Voer notities of opmerkingen in',
        section: 'main',
      },

      getValue: (asset) => asset.notes,
      formatDisplay: (value) => value || '-',
      buildUpdatePayload: (value) => ({ notes: value }),
    },
  ],

  // ============ Notes Configuration ============
  notes: {
    enabled: true,
    field: 'notes',
    label: 'Notities',
    placeholder: 'Voer notities in voor deze voorziening',
  },

  // ============ Navigation ============
  navigation: {
    backRoute: '/assets',
    useFiltering: true,
    enableInterPageNav: true,

    // Custom navigation handler to route to /asset/:assetId with preserved params
    customNavigationHandler: (targetId, searchParams, navigate) => {
      const params = new URLSearchParams(searchParams);
      if (navigate) {
        navigate(`/asset/${targetId}?${params.toString()}`);
      }
    },

    // Assets can be filtered by building/unit from the panel
    customFilterLogic: (allAssets, searchParams) => {
      const buildingFilter = searchParams.get('building');
      const unitFilter = searchParams.get('unit');
      const roomFilter = searchParams.get('room');
      const categoryFilter = searchParams.get('category');
      const statusFilter = searchParams.get('status');

      let filtered = allAssets;

      if (buildingFilter) {
        filtered = filtered.filter((asset) => asset.building?.id === buildingFilter);
      }

      if (unitFilter) {
        filtered = filtered.filter((asset) => asset.unit?.id === unitFilter);
      }

      if (roomFilter) {
        filtered = filtered.filter((asset) => asset.room?.id?.toString() === roomFilter);
      }

      if (categoryFilter) {
        filtered = filtered.filter(
          (asset) => asset.assetCategory?.id?.toString() === categoryFilter
        );
      }

      if (statusFilter) {
        filtered = filtered.filter((asset) => asset.status === statusFilter);
      }

      return filtered;
    },
  },

  // ============ Display Configuration ============
  titleField: 'name',

  getMetadataChips: (asset) => {
    const chips = [];

    // Status chip
    if (asset.status) {
      const StatusIcon = getStatusIcon(asset.status);
      const statusLabels: Record<string, string> = {
        [AssetStatus.ACTIVE]: 'Actief',
        [AssetStatus.INACTIVE]: 'Inactief',
        [AssetStatus.MAINTENANCE]: 'Onderhoud',
        [AssetStatus.DEFECT]: 'Defect',
        [AssetStatus.RETIRED]: 'Buiten gebruik',
        [AssetStatus.DECOMMISSIONED]: 'Ontmanteld',
        [AssetStatus.PENDING_INSTALLATION]: 'Wacht op installatie',
      };
      chips.push({
        label: statusLabels[asset.status] || asset.status,
        color: `border ${getStatusColor(asset.status)}`,
        icon: StatusIcon,
      });
    }

    // Category chip (with custom icon if configured)
    if (asset.assetCategory) {
      const catIcon = asset.assetCategory.icon ? getIconByValue(asset.assetCategory.icon) : null;
      chips.push({
        label: asset.assetCategory.name,
        icon: catIcon || Stack,
        color: 'border border-blue-400 bg-blue-50 text-blue-700',
      });
    }

    // Type chip (with custom icon if configured)
    if (asset.assetType) {
      const typeIcon = asset.assetType.icon ? getIconByValue(asset.assetType.icon) : null;
      chips.push({
        label: asset.assetType.name,
        icon: typeIcon || Package,
        color: 'border border-purple-400 bg-purple-50 text-purple-700',
      });
    }

    // Building chip
    if (asset.building) {
      chips.push({
        label: asset.building.id,
        icon: Buildings,
        color: 'border border-moss-primary/30 bg-moss-primary/10 text-moss-primary',
      });
    }

    // Unit chip
    if (asset.unit) {
      chips.push({
        label: `Unit ${asset.unit.id}`,
        icon: HouseLine,
        color: 'border border-moss-primary/30 bg-moss-primary/10 text-moss-primary',
      });
    }

    // Room chip
    if (asset.room) {
      chips.push({
        label: asset.room.roomType || asset.room.name || `Ruimte ${asset.room.id}`,
        icon: Door,
        color: 'border border-gray-400 bg-gray-50 text-gray-700',
      });
    }

    // Installation date chip
    if (asset.installationDate) {
      chips.push({
        label: `Geïnstalleerd: ${formatDate(asset.installationDate)}`,
        icon: Calendar,
        color: 'border border-gray-300 bg-gray-50 text-gray-600',
      });
    }

    // SharePoint folder chip
    if (asset.sharePointFolderUrl) {
      chips.push({
        label: 'SharePoint map',
        icon: FolderOpen,
        color: 'border border-sky-400 bg-sky-50 text-sky-700',
        href: asset.sharePointFolderUrl,
      });
    }

    return chips;
  },

  loadingText: 'Voorziening laden...',
  notFoundText: 'Voorziening niet gevonden',

  // ============ Plugins & Custom Behavior ============
  plugins: {
    // No automatic actions on load
    onEntityLoad: undefined,

    // Custom action buttons
    actions: [
      {
        id: 'delete',
        label: 'Verwijderen',
        icon: Trash,
        variant: 'danger',
        confirmMessage: 'Weet u zeker dat u deze voorziening wilt verwijderen? Dit kan niet ongedaan worden gemaakt.',
        handler: async (entity, session, _onUpdate) => {
          try {
            const response = await fetchWithAuth(
              `${API_URL}/assets/${entity.id}`,
              { method: 'DELETE' },
              session
            );

            if (!response.ok) {
              throw new Error('Failed to delete asset');
            }

            toast.success('Voorziening succesvol verwijderd');

            // Navigate back to assets list
            window.location.href = '/assets';
          } catch (error) {
            console.error('Error deleting asset:', error);
            toast.error('Fout bij verwijderen van voorziening');
            throw error;
          }
        },
      },
    ],

    // Custom sections for file logs and pictures will be added in the detail page
    customSections: [],
  },
};
